<template>
  <form
    ref="form"
    novalidate
    class="container px-0 d-flex flex-column justify-content-between needs-validation"
    @submit="submit"
  >
    <div class="flex-grow-1">
      <div class="row">
        <BootstrapFormControl
          v-model="modelName"
          name="Component Name"
          class="col-4"
          required
          :error="errorMessages.get('name')"
          :server-error="serverError"
        />
        <BootstrapSelect
          v-model="modelComponentType"
          name="Component Type"
          class="col-4"
          :options="componentTypeOptions"
          required
          :error="errorMessages.get('component_type_id')"
          :server-error="serverError"
        />
        <BootstrapSelect
          v-model="modelCostPool"
          name="Cost Pool"
          class="col-4"
          :options="costPoolOptions"
          required
          :error="errorMessages.get('cost_pool_id')"
          :server-error="serverError"
        />
      </div>
      <div class="row">
        <BootstrapSelect
          v-model="modelProductOffering"
          name="Product Offering"
          class="col-4"
          :options="productOfferingOptions"
          :error="errorMessages.get('productOffering')"
          :server-error="serverError"
        />
      </div>
      <div class="row">
        <BootstrapFormControl
          v-model="modelDescription"
          name="Component Description"
          class="col"
          required
          :error="errorMessages.get('description')"
          :server-error="serverError"
          textarea
        />
      </div>
      <section v-if="costPoolType === 'direct'" class="row">
        <div class="col">
          <div class="col card bg-body-secondary">
            <div class="card-header">
              <h5 class="my-1">Direct Cost</h5>
            </div>
            <div class="card-body">
              <div class="container p-0">
                <div class="row">
                  <BootstrapFormControl
                    v-model="modelDirectCostIdentifier"
                    name="Direct Cost Identifier"
                    class="col"
                    required
                    :error="errorMessages.get('direct_cost_identifier')"
                    :server-error="serverError"
                  />
                  <BootstrapFormControl
                    v-model="modelDirectCostWeight"
                    name="Direct Cost Weight"
                    class="col"
                    required
                    :error="errorMessages.get('direct_cost_weight')"
                    :server-error="serverError"
                  />
                </div>
                <div class="row">
                  <BootstrapFormControl
                    v-model="modelDirectCostWeightDescription"
                    name="Direct Cost Weight Description"
                    class="col"
                    required
                    :error="errorMessages.get('direct_cost_weight_description')"
                    :server-error="serverError"
                    textarea
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section v-if="costPoolType === 'indirect'" class="row">
        <div class="col">
          <div class="col card bg-body-secondary">
            <div class="card-header">
              <h5 class="my-1">Indirect Cost</h5>
            </div>
            <div class="card-body">
              <div class="container p-0">
                <div class="row">
                  <BootstrapFormControl
                    v-model="modelEstimatedCostUnits"
                    name="Estimated Cost Units"
                    class="col-6"
                    required
                    :error="errorMessages.get('estimated_cost_units')"
                    :server-error="serverError"
                  />
                </div>
                <div class="row">
                  <BootstrapFormControl
                    v-model="modelEstimatedCostDescription"
                    name="Estimated Cost Description"
                    class="col"
                    required
                    :error="errorMessages.get('estimated_cost_description')"
                    :server-error="serverError"
                    textarea
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section class="row">
        <div class="col">
          <div class="card bg-body-secondary">
            <div class="card-header">
              <h5 class="my-1">GDPR Information</h5>
            </div>
            <ListGroup class="mx-3" :default-active-id="''">
              <BootstrapCheck
                id="modelPersonalDataStored"
                v-model="modelPersonalDataStored"
                :label="'Personal data stored'"
              />
              <BootstrapCheck
                id="modelOemCanAccessData"
                v-model="modelOemCanAccessData"
                :label="`Personal data is visible for OEM`"
              />
              <BootstrapCheck
                id="modelResellerCanAccessData"
                v-model="modelResellerCanAccessData"
                :label="`Personal data is visible for Distributor`"
              />
            </ListGroup>
          </div>
        </div>
      </section>
      <div class="row mt-3">
        <div class="d-flex w-100">
          <BootstrapButton
            name="submit"
            class="me-2"
            type="submit"
            :disabled="disabled || loading"
            :loading="loading"
            @click.stop
          >
            Save
          </BootstrapButton>
          <BootstrapButton name="cancel" variant="secondary" @click="router.go(-1)">
            Cancel
          </BootstrapButton>
        </div>
      </div>
    </div>
  </form>
</template>
<script lang="ts" setup>
const props = defineProps<{
  componentType: string;
  componentTypeOptions: string[];
  costPool: string;
  costPoolOptions: string[];
  costPoolType: string;
  description: string;
  directCostIdentifier: string;
  directCostWeight: number;
  directCostWeightDescription: string;
  errorMessages: Map<any, any>;
  estimatedCostUnits: number;
  estimatedCostDescription: string;
  loading: boolean;
  name: string;
  oemCanAccessData: boolean;
  personalDataStored: boolean;
  productOffering: string;
  productOfferingOptions: string[];
  resellerCanAccessData: boolean;
  serverError: boolean;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  'update:componentType': [value: string];
  'update:costPool': [value: string];
  'update:description': [value: string];
  'update:directCostIdentifier': [value: string];
  'update:directCostWeight': [value: number];
  'update:directCostWeightDescription': [value: string];
  'update:estimatedCostUnits': [value: number];
  'update:estimatedCostDescription': [value: string];
  'update:name': [value: string];
  'update:oemCanAccessData': [value: boolean];
  'update:personalDataStored': [value: boolean];
  'update:productOffering': [value: string];
  'update:resellerCanAccessData': [value: boolean];
  submit: [form: globalThis.Ref<HTMLFormElement | null>];
}>();

const router = useRouter();

// Template ref
const form = ref<HTMLFormElement | null>(null);

// Models
const modelComponentType = computed({
  get: () => props.componentType,
  set: (value) => emit('update:componentType', value),
});
const modelCostPool = computed({
  get: () => props.costPool,
  set: (value) => emit('update:costPool', value),
});
const modelDescription = computed({
  get: () => props.description,
  set: (value) => emit('update:description', value),
});
const modelDirectCostIdentifier = computed({
  get: () => props.directCostIdentifier,
  set: (value) => emit('update:directCostIdentifier', value),
});
const modelDirectCostWeight = computed({
  get: () => String(props.directCostWeight),
  set: (value) => emit('update:directCostWeight', Number(value)),
});
const modelDirectCostWeightDescription = computed({
  get: () => props.directCostWeightDescription,
  set: (value) => emit('update:directCostWeightDescription', value),
});
const modelEstimatedCostUnits = computed({
  get: () => String(props.estimatedCostUnits),
  set: (value) => emit('update:estimatedCostUnits', Number(value)),
});
const modelEstimatedCostDescription = computed({
  get: () => props.estimatedCostDescription,
  set: (value) => emit('update:estimatedCostDescription', value),
});
const modelName = computed({
  get: () => props.name,
  set: (value) => emit('update:name', value),
});
const modelOemCanAccessData = computed({
  get: () => props.oemCanAccessData,
  set: (value) => emit('update:oemCanAccessData', value),
});
const modelPersonalDataStored = computed({
  get: () => props.personalDataStored,
  set: (value) => emit('update:personalDataStored', value),
});
const modelProductOffering = computed({
  get: () => props.productOffering,
  set: (value) => emit('update:productOffering', value),
});
const modelResellerCanAccessData = computed({
  get: () => props.resellerCanAccessData,
  set: (value) => emit('update:resellerCanAccessData', value),
});

// https://getbootstrap.com/docs/4.6/components/forms/?#how-it-works
const submit = async (event: Event) => {
  if (form.value?.checkValidity() === false) {
    event.preventDefault();
    event.stopPropagation();
    form.value.classList.add('was-validated');
  } else {
    event.preventDefault();
    emit('submit', form);
  }
};
</script>
